import styled, { css, keyframes, DefaultTheme } from 'styled-components';
import Text from '@components/text';
import { defaultTheme } from '@styles/theme.styled';
import { generateScales } from '@helpers/generate-media-queries';
import { wrapperWidths } from '@components/wrapper.styled';
import { underlineTransition } from '@components/link/link.styled';
import { StyledMediumButton } from '@components/button/button.styled';

/**
 * Styled text component for cookie consent messages
 * Handles responsive font sizes and link styling
 */
export const CookieText = styled(Text)`
  ${({ theme }) => css`
    display: block;
    color: ${theme.colors.black};
    font-size: 1rem;
    font-family: ${theme.fontFamily.text};
    line-height: 1.375;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      font-size: 0.9375rem;
      line-height: 1.25;
    }

    a {
      line-height: 1.375;
      color: ${theme.colors.lightPurple};
      ${underlineTransition}

      @media only screen and (max-width: ${theme.breakpoints.sm}) {
        line-height: 1.25;
      }
    }
  `}
`;

/**
 * Animation keyframes for revealing the cookie consent banner
 * @param offset - The vertical offset for the animation
 */
const reveal = (offset: string) => keyframes`
  from {
    opacity: 0;
    transform: translateY(${offset});
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Common styles for the consent container
 * Handles responsive layout and animations
 */
const consentContainer = (theme: DefaultTheme) => css`
  .consent-container {
    line-height: ${theme.lineHeights.lg};
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 2rem;
    background: ${theme.colors.white};
    box-shadow: 0 0 4rem rgba(0, 0, 0, 0.25);
    font-family: ${theme.fontFamily.display};
    padding: 1.25rem 2rem;
    font-size: 1rem;
    border-radius: 1rem;
    font-weight: 400;
    animation: 0.375s ${reveal('2rem')} ease-out;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      flex-direction: column;
      justify-items: flex-start;
      row-gap: 1rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      animation: 0.375s ${reveal('-1rem')} ease-in-out;
      padding: 1.25rem 1rem;
      margin-bottom: 0;
      box-shadow: 0rem 0rem 1rem rgba(0, 0, 0, 0.25);
      border-radius: 0.5rem;
      row-gap: 0.75rem;
    }
  }
`;

/**
 * Styles for the consent button wrapper
 * Handles responsive layout and spacing
 */
const consentBtnWrapper = (theme: DefaultTheme) => css`
  .consent-btn-wrapper {
    display: flex;
    column-gap: 0.75rem;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      column-gap: 0.5rem;
      width: 100%;
      padding: 0;
    }
  }
`;

/**
 * Props for the CookieConsentWrapper component
 */
export interface CookieConsentWrapperProps {
  /** Background color key from theme */
  bgColor?: keyof typeof defaultTheme.backgroundColors;
}

/**
 * Main wrapper component for the cookie consent banner
 * Handles positioning and responsive layout
 */
export const CookieConsentWrapper = styled.div<CookieConsentWrapperProps>`
  ${({ theme, bgColor }) => css`
    position: fixed;
    left: 50%;
    bottom: 1rem;
    z-index: 99;
    width: ${wrapperWidths.xxl};
    transform: translateX(-50%);
    padding: 0 0;

    @media only screen and (max-width: ${theme.breakpoints.xxl}) {
      width: ${wrapperWidths.xl};
    }

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      width: ${wrapperWidths.xl};
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      width: ${wrapperWidths.lg};
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      width: ${wrapperWidths.md};
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      position: fixed;
      bottom: 1rem;
      left: 0.5rem;
      right: 0.5rem;
      width: auto;
      transform: none;
      background-color: transparent;
      padding: 0.5rem;
    }

    ${consentContainer(theme)}
    ${consentBtnWrapper(theme)}
  `}
`;

/**
 * Styles for the secondary button variant
 */
const secondaryButton = (theme: DefaultTheme) => css`
  &:first-child {
    color: ${theme.colors.black};
    background: transparent;
    border-width: 1.5px;
    border-color: ${theme.colors.black};
    font-weight: 500;

    &:hover {
      color: ${theme.colors.white};
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primary};
    }
  }
`;

/**
 * Styles for the primary button variant
 */
const primaryButton = (theme: DefaultTheme) => css`
  background: ${theme.colors.primary};
  border-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  font-weight: 500;

  &:hover {
    background: ${theme.colors.black};
    border-color: ${theme.colors.black};
  }
`;

/**
 * Styled button component for cookie consent actions
 * Extends the base medium button with custom styling
 */
export const StyledButton = styled(StyledMediumButton)`
  ${({ theme }) => css`
    font-weight: 500;
    white-space: nowrap;
    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.body.desktop, theme)}

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      height: 2.25rem;
      padding: 0 1.125rem;
    }

    ${primaryButton(theme)}
    ${secondaryButton(theme)}
  `}
`;
